<script setup>
import { onMounted, ref, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { login } from '@/api/admin'
import router from '@/router'
import { useAdminStore } from '@/stores/admin'

const emit = defineEmits(['toggleLoadingOn', 'toggleLoadingOff']);




const { t } = useI18n()
const adminStore = useAdminStore()

const form = ref({
    email: '',
    password: ''
})

const formError = ref({
    email: { error: false, message: '' },
    password: { error: false, message: '' },
    credentials: { error: false, message: t('admin.errors.invalid_credentials') }
})

const step = ref(1)

const validateForm = () => {
    let hasError = false

    if (!form.value.email) {
        formError.value.email.error = true
        formError.value.email.message = t('admin.errors.email_required')
        hasError = true
    } else {
        formError.value.email.error = false
        formError.value.email.message = ''
    }

    if (!form.value.password) {
        formError.value.password.error = true
        formError.value.password.message = t('admin.errors.password_required')
        hasError = true
    } else if (form.value.password.length < 8) {
        formError.value.password.error = true
        formError.value.password.message = t('admin.errors.password_invalid')
        hasError = true
    } else {
        formError.value.password.error = false
        formError.value.password.message = ''
    }

    return !hasError
}

const submitForm = async () => {

    emit('toggleLoadingOn');
    if (!validateForm()) {
        emit('toggleLoadingOff');
        return;
    }

    try {
        let response = await login(form.value)
        if (response.data.success) {
            formError.value.credentials.error = false;
            console.log('response.data :>> ', response.data);
            adminStore.setAdmin(response.data)
            router.push('admin/dashboard')
            emit('toggleLoadingOff');
        }
    } catch (error) {
        emit('toggleLoadingOff');
        formError.value.credentials.error = true
        console.error('Login error:', error)
    }

}



</script>

<template>
    <div class="w-full h-full flex items-center justify-center px-5 lg:px-0"
        :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
        <div
            class="h-auto bg-white border border-gray-200 shadow-lg rounded-xl px-6 py-6 w-full lg:w-fit space-y-3 flex flex-col justify-center">

            <!-- Title -->
            <div class="h-2/3 w-full flex flex-col justify-center items-center">
                <div class="text-center">
                    <h1 class="text-2xl font-bold text-primary">{{ $t('admin.title') }}</h1>
                    <p class="text-gray-500 text-sm mt-2">{{ $t('admin.subtitle') }}</p>
                </div>

                <!-- Form -->
                <div class="w-full mt-3">
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        {{ $t('admin.email') }}
                    </label>
                    <input type="text" v-model="form.email" :disabled="step === 2" :class="[
                        'w-full border rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100 transition duration-200',
                        formError.email.error ? 'border-red-400' : 'border-gray-300'
                    ]" />
                    <p v-if="formError.email.error" class="text-red-500 text-xs mt-1">
                        {{ formError.email.message }}
                    </p>
                </div>

                <div class="w-full mt-3">
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        {{ $t('admin.password') }}
                    </label>
                    <input type="password" v-model="form.password" :disabled="step === 2" :class="[
                        'w-full border rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100 transition duration-200',
                        formError.password.error ? 'border-red-400' : 'border-gray-300'
                    ]" />
                    <p v-if="formError.password.error" class="text-red-500 text-xs mt-1">
                        {{ formError.password.message }}
                    </p>
                </div>

                <p v-if="formError.credentials.error" class="text-red-500 text-xs mt-1">
                    {{ formError.credentials.message }}
                </p>
            </div>

            <!-- Submit Button -->
            <div class="h-1/3 w-full">
                <div v-if="step === 1" class="pt-2">
                    <button @click="submitForm"
                        class="w-full bg-primary text-white rounded-lg py-2 hover:bg-secondary transition duration-200">
                        {{ $t('admin.submit') }}
                    </button>
                </div>
            </div>

            <div class="text-center mt-2">
                <div class="text-black  text-sm">
                    {{ $t('admin.forgot_password') }} <span @click="router.push('/admin/forgot-password')"
                        class="text-primary underline"> {{ $t('admin.recover_password') }} </span>
                </div>
            </div>

        </div>
    </div>
</template>

<style scoped>
@keyframes bounce-wave {

    0%,
    80%,
    100% {
        transform: translateY(0);
    }

    40% {
        transform: translateY(-8px);
    }
}

.animate-bounce-wave {
    animation: bounce-wave 1.2s infinite ease-in-out;
}

.delay-0 {
    animation-delay: 0s;
}

.delay-150 {
    animation-delay: 0.15s;
}

.delay-300 {
    animation-delay: 0.3s;
}

/* Remove arrows for Chrome, Safari, Edge, and Opera */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Remove arrows for Firefox */
input[type="number"] {
    -moz-appearance: textfield;
}
</style>
